import { z } from 'zod';
/**
 * Accountability Chart — EOS's replacement for the org chart. It maps
 * *seats* (roles with 3–5 named accountabilities) rather than people.
 * Multiple seats can be held by one person ("one butt, several hats");
 * empty seats expose hiring gaps. The chart is graph-shaped: each Seat
 * has a single `parentSeatId` pointer, and tracing pointers up reveals
 * the reporting structure.
 *
 * Storage path: `seats/{seatId}`. Reads gated by `orgId`. The "Visionary"
 * seat at the top has `parentSeatId: null`; everything else points at a
 * parent. Rendering walks the tree with a DFS keyed off `sortIndex` for
 * sibling order.
 */
export const SEATS_COLLECTION = 'seats';
export const Seat = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** Role title — "Integrator", "VP Sales", "Director of Operations", etc. */
    title: z.string().min(1),
    /** 3–5 short phrases describing what this seat is responsible for. */
    accountabilities: z.array(z.string()).default([]),
    /**
     * Pointer to the seat this one reports into. `null` only on the root
     * seat (typically Visionary); everywhere else this is required.
     */
    parentSeatId: z.string().nullable(),
    /**
     * Uid of the person currently in the seat. `null` means the seat is
     * vacant — surfaced in the chart UI as a hiring gap.
     */
    holderUid: z.string().nullable(),
    /** Sibling order under the same parent. Drag-reorder updates this. */
    sortIndex: z.number().int().nonnegative(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Body for `POST /api/seats`. */
export const CreateSeatRequest = z.object({
    title: z.string().min(1),
    accountabilities: z.array(z.string()).default([]),
    parentSeatId: z.string().nullable(),
    holderUid: z.string().nullable().optional(),
    sortIndex: z.number().int().nonnegative(),
});
/** Body for `PATCH /api/seats/:id`. */
export const UpdateSeatRequest = z.object({
    title: z.string().min(1).optional(),
    accountabilities: z.array(z.string()).optional(),
    parentSeatId: z.string().nullable().optional(),
    holderUid: z.string().nullable().optional(),
    sortIndex: z.number().int().nonnegative().optional(),
});
